import { Controller } from "@hotwired/stimulus"
import { reportsIntroAlreadyPlayed } from "./reveal_controller"

// Renders a counter value into an element using the element's own
// data attributes (split-decimal, smart-abbreviate, abbreviate).
// Exported so turbo_cache_controller can freeze counters at their final
// value when snapshotting a page or a fresh body replacing a preview.
export function renderCounterValue(el, value, precision = 2) {
  const formatted = formatCounterValue(el, value, precision)
  if (el.dataset.counterSplitDecimal === "true" && formatted.includes(".")) {
    const [intPart, decPart] = formatted.split(".")
    el.innerHTML = `${intPart}<span class="counter-decimal">.${decPart}</span>`
  } else {
    el.textContent = formatted
  }
}

function formatCounterValue(el, value, precision) {
  // Smart abbreviation: <1K full w/ decimals, 1K–9.99K full no decimals,
  // 10K+ → "12.3K", 1M+ → "1.2M", 1B+ → "1.2B". Keeps small amounts precise
  // while guaranteeing big numbers fit in tight cells.
  if (el.dataset.counterSmartAbbreviate === "true") {
    const abs = Math.abs(value)
    if (abs >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"
    if (abs >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"
    if (abs >= 10_000) return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K"
    if (abs >= 1000) return withDelimiter(Math.round(value).toString())
    return withDelimiter(value.toFixed(2)).replace(/\.?0+$/, "")
  }
  // Legacy: abbreviate everything >= 1000
  if (el.dataset.counterAbbreviate === "true" && value >= 1000) {
    return (value / 1000).toFixed(1) + "K"
  }
  if (precision > 0) {
    return withDelimiter(value.toFixed(2)).replace(/\.?0+$/, "")
  }
  return withDelimiter(Math.round(value).toString())
}

// Adds thousand separators (commas) to the integer portion of a numeric string,
// preserving any decimal part. "100300.50" -> "100,300.50"
function withDelimiter(numStr) {
  const [intPart, decPart] = numStr.split(".")
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  return decPart === undefined ? withCommas : `${withCommas}.${decPart}`
}

// Displays a formatted number. By default it renders immediately; with
// data-counter-animate-value="true" it counts up from 0 (easeOutExpo) the
// first time it scrolls into view. Respects prefers-reduced-motion.
// Usage: <span data-controller="counter" data-counter-target-value="325.50">0</span>
export default class extends Controller {
  static values = {
    target: Number,
    duration: { type: Number, default: 800 },
    precision: { type: Number, default: 2 },
    animate: { type: Boolean, default: false }
  }

  connect() {
    // animateValue must be checked first: only reports counters opt into the
    // intro, so counters on other pages never touch the played-once flag.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (!this.animateValue || reduced || reportsIntroAlreadyPlayed()) {
      this.render(this.targetValue)
      return
    }

    this.render(0)
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        this.observer.disconnect()
        this.countUp()
      })
    }, { threshold: 0.4 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  countUp() {
    const start = performance.now()
    const step = (now) => {
      const progress = Math.min((now - start) / this.durationValue, 1)
      const eased = 1 - Math.pow(2, -10 * progress)
      this.render(progress === 1 ? this.targetValue : this.targetValue * eased)
      if (progress < 1) this.frame = requestAnimationFrame(step)
    }
    this.frame = requestAnimationFrame(step)
  }

  render(value) {
    renderCounterValue(this.element, value, this.precisionValue)
  }
}
